import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Check, Door, Prohibit } from "@phosphor-icons/react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const badgeVariants = cva("flex w-max items-center justify-end gap-1 rounded-sm", {
  variants: {
    variant: {
      default: "",
      success: "text-caption! text-on-success-surface! truncate",
    },
    background: {
      default: "text-caption bg-surface-subdued inline-flex h-5 items-center gap-1 px-2",
      success: "text-caption bg-success-surface-subdued inline-flex h-5 items-center gap-1 rounded-sm px-2",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

type LabelType =
  | "GATEKEEPER"
  | "PREFERRED"
  | "NOT_RECOMMENDEDED"
  | "NO_NEW_PATIENTS"
  | "IS_MPN"
  | "ONLY_MPN_ALLOWED"
  | "INSURER_NETWORK"
  | "NONE"

interface CareProviderTagLayoutProps {
  logo: ReactNode
  label: LabelType
  variant: VariantProps<typeof badgeVariants>["variant"]
  background: VariantProps<typeof badgeVariants>["background"]
  testId?: string
}

type LabelTranslations = {
  label: LabelType
  value: string
}

const labelTypes: LabelTranslations[] = [
  { label: "GATEKEEPER", value: "admin.careProviders.tag.gateKeeper" },
  { label: "PREFERRED", value: "admin.careProviders.tag.preferred" },
  { label: "NO_NEW_PATIENTS", value: "admin.careProviders.tag.noNewPatients" },
  { label: "NOT_RECOMMENDEDED", value: "admin.careProviders.tag.notRecommended" },
  { label: "IS_MPN", value: "admin.careProviders.tag.isMPN" },
  { label: "ONLY_MPN_ALLOWED", value: "admin.careProviders.tag.onlyMPNAllowed" },
  { label: "INSURER_NETWORK", value: "admin.careProviders.tag.insurerNetwork" },
]

const CareProviderTagLayout = ({ logo, label, variant, background, testId }: CareProviderTagLayoutProps) => {
  const { t } = useTranslation()

  const currentLabel: LabelTranslations = labelTypes.findLast((l) => l.label === label) ?? { label: "NONE", value: "" }

  if (currentLabel.label === "NONE") return null

  return (
    <span className={cn(badgeVariants({ background, variant }))} data-testid={testId}>
      <span>{logo}</span>
      <span className={cn(badgeVariants({ variant }))}>
        <small>{t(currentLabel?.value)}</small>
      </span>
    </span>
  )
}

const CareProviderTag = () => null

const GateKeeper = () => {
  return (
    <CareProviderTagLayout
      label="GATEKEEPER"
      logo={<Door size={15} />}
      variant="default"
      background="success"
      testId="care-provider-gatekeeper-tag"
    />
  )
}

const MedgatePartnerNetwork = () => {
  return (
    <CareProviderTagLayout
      label="IS_MPN"
      logo={<Check size={15} />}
      variant="default"
      background="success"
      testId="care-provider-mpn-tag"
    />
  )
}

const InsurerNetwork = () => {
  return (
    <CareProviderTagLayout
      label="INSURER_NETWORK"
      logo={<Check size={15} />}
      variant="default"
      background="success"
      testId="care-provider-insurer-network-tag"
    />
  )
}

const OnlyMPNAllowed = () => {
  return (
    <CareProviderTagLayout
      label="ONLY_MPN_ALLOWED"
      logo={<Prohibit size={15} />}
      variant="default"
      background="default"
      testId="care-provider-only-mpn-allowed-tag"
    />
  )
}

const Preferred = () => {
  return (
    <CareProviderTagLayout
      label="PREFERRED"
      logo={<Check size={15} />}
      variant="success"
      background="success"
      testId="care-provider-preferred-tag"
    />
  )
}

const NotRecommended = () => {
  return (
    <CareProviderTagLayout
      label="NOT_RECOMMENDEDED"
      logo={<Prohibit size={15} />}
      variant="default"
      background="default"
      testId="care-provider-not-recommended-tag"
    />
  )
}

const NoNewPatients = () => {
  return (
    <CareProviderTagLayout
      label="NO_NEW_PATIENTS"
      logo={<Prohibit size={15} />}
      variant="default"
      background="default"
      testId="care-provider-no-new-patients-tag"
    />
  )
}

CareProviderTag.GateKeeper = GateKeeper
CareProviderTag.Preferred = Preferred
CareProviderTag.NotRecommended = NotRecommended
CareProviderTag.NoNewPatients = NoNewPatients
CareProviderTag.MedgatePartnerNetwork = MedgatePartnerNetwork
CareProviderTag.OnlyMPNAllowed = OnlyMPNAllowed
CareProviderTag.InsurerNetwork = InsurerNetwork

export default CareProviderTag
